import type { QueryClient, QueryKey } from '@tanstack/react-query'
import { buildApiUrl } from './api'
import { env } from './env'

type RealtimePayload = {
  type?: unknown
  name?: unknown
  event?: unknown
  topic?: unknown
}

type RealtimeClientOptions = {
  queryClient: QueryClient
  onUnavailable?: () => void
}

const MAX_RECONNECT_ATTEMPTS = 3
const BASE_RECONNECT_DELAY_MS = 5_000
const MAX_RECONNECT_DELAY_MS = 60_000

const invalidationsByType: Record<string, QueryKey[]> = {
  '*': [
    ['agents'],
    ['businesses'],
    ['dashboard'],
    ['exchange-packs'],
    ['exchange-requests'],
    ['iacrm'],
    ['notifications'],
    ['points'],
    ['programs'],
    ['prospects'],
    ['sync'],
    ['transactions'],
  ],
  'agent.activated': [['agents'], ['dashboard'], ['notifications']],
  'agent.invited': [['agents'], ['dashboard'], ['notifications']],
  'agent.reactivated': [['agents'], ['dashboard'], ['notifications']],
  'agent.suspended': [['agents'], ['dashboard'], ['notifications']],
  'agent.updated': [['agents'], ['dashboard'], ['notifications']],
  'business.approved': [['businesses'], ['dashboard'], ['notifications']],
  'business.created': [['businesses'], ['dashboard'], ['notifications']],
  'business.updated': [['businesses'], ['dashboard'], ['notifications']],
  'exchange.approved': [['exchange-requests'], ['notifications'], ['points'], ['transactions']],
  'exchange.cancelled': [['exchange-requests'], ['notifications'], ['points']],
  'exchange.completed': [['exchange-requests'], ['notifications'], ['points'], ['transactions']],
  'exchange.created': [['exchange-requests'], ['notifications'], ['points']],
  'exchange.processing': [['exchange-requests'], ['notifications']],
  'exchange.rejected': [['exchange-requests'], ['notifications'], ['points']],
  'exchange.requested': [['exchange-requests'], ['notifications'], ['points']],
  'exchange.updated': [['exchange-requests'], ['notifications'], ['points'], ['transactions']],
  'exchange_pack.updated': [['exchange-packs'], ['programs']],
  'iacrm.updated': [['iacrm'], ['sync']],
  'iacrm.invoice.synced': [['iacrm', 'invoices'], ['points'], ['transactions'], ['dashboard'], ['notifications']],
  'iacrm.prospect.stage_updated': [['iacrm', 'pipeline'], ['prospects'], ['dashboard'], ['notifications']],
  'notification.created': [['notifications']],
  'notification.read': [['notifications']],
  'notification.read_all': [['notifications']],
  'points.updated': [['points'], ['transactions'], ['exchange-requests'], ['dashboard']],
  'program.created': [['programs'], ['dashboard']],
  'program.updated': [['programs'], ['dashboard']],
  'prospect.converted': [['prospects'], ['transactions'], ['points'], ['dashboard'], ['notifications']],
  'prospect.submitted': [['prospects'], ['dashboard'], ['notifications']],
  'prospect.updated': [['prospects'], ['dashboard'], ['notifications']],
  'sync.completed': [['sync'], ['iacrm'], ['prospects'], ['transactions'], ['points'], ['dashboard']],
  'transaction.created': [['transactions'], ['points'], ['prospects'], ['dashboard'], ['notifications']],
  'transaction.paid': [['transactions'], ['points'], ['prospects'], ['dashboard'], ['notifications']],
  'transaction.updated': [['transactions'], ['points'], ['prospects'], ['dashboard'], ['notifications']],
}

const namedEventTypes = Object.keys(invalidationsByType).filter((type) => type !== '*')

function getPayloadType(payload: RealtimePayload) {
  const candidates = [payload.type, payload.name, payload.event, payload.topic]

  return candidates.find((candidate): candidate is string => (
    typeof candidate === 'string' && candidate.length > 0
  ))
}

function parsePayload(data: string): RealtimePayload | null {
  try {
    const parsed = JSON.parse(data) as RealtimePayload
    return parsed && typeof parsed === 'object' ? parsed : null
  } catch {
    return null
  }
}

function invalidateForType(queryClient: QueryClient, type: string) {
  const queryKeys = invalidationsByType[type] ?? (type === 'invalidate.all' ? invalidationsByType['*'] : [])

  for (const queryKey of queryKeys) {
    queryClient.invalidateQueries({ queryKey })
  }
}

function handlePayload(queryClient: QueryClient, data: string, fallbackType?: string) {
  const payload = parsePayload(data)
  const type = payload ? getPayloadType(payload) ?? fallbackType : fallbackType

  if (type) {
    invalidateForType(queryClient, type)
  }
}

export function startRealtimeEvents({ queryClient, onUnavailable }: RealtimeClientOptions) {
  if (!env.realtimeEventsEnabled || typeof window === 'undefined' || typeof window.EventSource === 'undefined') {
    return () => undefined
  }

  let source: EventSource | null = null
  let reconnectTimer: number | undefined
  let reconnectAttempts = 0
  let stopped = false

  const cleanupSource = () => {
    source?.close()
    source = null
  }

  const connect = () => {
    if (stopped || source !== null) {
      return
    }

    source = new EventSource(buildApiUrl(env.realtimeEventsPath), { withCredentials: true })

    source.onopen = () => {
      reconnectAttempts = 0
    }

    source.onmessage = (message) => {
      handlePayload(queryClient, message.data)
    }

    for (const type of namedEventTypes) {
      source.addEventListener(type, (message) => {
        handlePayload(queryClient, message.data, type)
      })
    }

    source.onerror = () => {
      cleanupSource()
      reconnectAttempts += 1

      if (reconnectAttempts > MAX_RECONNECT_ATTEMPTS) {
        onUnavailable?.()
        return
      }

      const delay = Math.min(
        BASE_RECONNECT_DELAY_MS * 2 ** (reconnectAttempts - 1),
        MAX_RECONNECT_DELAY_MS,
      )

      reconnectTimer = window.setTimeout(connect, delay)
    }
  }

  connect()

  return () => {
    stopped = true

    if (reconnectTimer !== undefined) {
      window.clearTimeout(reconnectTimer)
    }

    cleanupSource()
  }
}
